import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, where, limit
} from 'firebase/firestore';
import Sidebar from '../Shared/Sidebar';
import { DEFAULT_RATING, getTier, getLevelBadge } from '../../utils/chessRating';

const EMPTY_FORM = {
  name: '', school: '', age: '', studentClass: '',
  level: 'Beginner', rating: DEFAULT_RATING,
};

export default function ChessStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [records, setRecords] = useState({}); // studentId -> games[]
  const [recordsLoading, setRecordsLoading] = useState(false);

  useEffect(() => { fetchStudents(); }, []);

  async function fetchStudents() {
    try {
      const snap = await getDocs(collection(db, 'chessStudents'));
      setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleCancel() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name,
        school: form.school,
        age: form.age,
        studentClass: form.studentClass,
        level: form.level,
        rating: Number(form.rating) || DEFAULT_RATING,
      };

      if (editingId) {
        await updateDoc(doc(db, 'chessStudents', editingId), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
        setSuccess('Chess student updated!');
      } else {
        await addDoc(collection(db, 'chessStudents'), {
          ...payload,
          wins: 0,
          losses: 0,
          draws: 0,
          gamesPlayed: 0,
          createdAt: serverTimestamp(),
        });
        setSuccess('Chess student registered!');
      }
      handleCancel();
      fetchStudents();
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  }

  function handleEdit(s) {
    setEditingId(s.id);
    setForm({
      name: s.name || '',
      school: s.school || '',
      age: s.age || '',
      studentClass: s.studentClass || '',
      level: s.level || 'Beginner',
      rating: s.rating ?? DEFAULT_RATING,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleDelete(id) {
    if (window.confirm('Are you sure you want to remove this chess student?')) {
      await deleteDoc(doc(db, 'chessStudents', id));
      fetchStudents();
    }
  }

  async function toggleRecords(studentId) {
    if (expandedId === studentId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(studentId);
    if (!records[studentId]) {
      setRecordsLoading(true);
      try {
        const [whiteSnap, blackSnap] = await Promise.all([
          getDocs(query(
            collection(db, 'chessGames'),
            where('white.studentId', '==', studentId),
            where('status', '==', 'finished'),
            limit(20)
          )),
          getDocs(query(
            collection(db, 'chessGames'),
            where('black.studentId', '==', studentId),
            where('status', '==', 'finished'),
            limit(20)
          )),
        ]);
        const games = [
          ...whiteSnap.docs.map(d => ({ id: d.id, color: 'white', ...d.data() })),
          ...blackSnap.docs.map(d => ({ id: d.id, color: 'black', ...d.data() })),
        ].sort((a, b) => (b.finishedAt?.seconds || 0) - (a.finishedAt?.seconds || 0));
        setRecords(prev => ({ ...prev, [studentId]: games }));
      } catch (err) {
        console.error(err);
      }
      setRecordsLoading(false);
    }
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Chess Students Registry</div>
            <div style={styles.pageSub}>Register students, track ratings & game records</div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => (showForm ? handleCancel() : setShowForm(true))}
          >
            {showForm ? 'Cancel' : '+ Register Student'}
          </button>
        </div>

        {success && <div style={styles.successMsg}>{success}</div>}
        {error && <div style={styles.errorMsg}>{error}</div>}

        {showForm && (
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              {editingId ? 'Edit Chess Student' : 'Register New Chess Student'}
            </div>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Full Name *</label>
                  <input style={styles.formInput} name="name" value={form.name} onChange={handleChange} placeholder="e.g. Baraka M." required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>School *</label>
                  <input style={styles.formInput} name="school" value={form.school} onChange={handleChange} placeholder="e.g. Kinondoni Primary" required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Age</label>
                  <input style={styles.formInput} name="age" type="number" min="3" max="25" value={form.age} onChange={handleChange} placeholder="e.g. 11" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Class</label>
                  <input style={styles.formInput} name="studentClass" value={form.studentClass} onChange={handleChange} placeholder="e.g. Class 5" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Chess Level *</label>
                  <select style={styles.formInput} name="level" value={form.level} onChange={handleChange}>
                    <option>Beginner</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Starting Rating</label>
                  <input style={styles.formInput} name="rating" type="number" value={form.rating} onChange={handleChange} />
                </div>
              </div>
              <button type="submit" style={styles.submitBtn} disabled={loading}>
                {loading
                  ? (editingId ? 'Updating...' : 'Registering...')
                  : (editingId ? 'Update Student' : 'Register Student')}
              </button>
            </form>
          </div>
        )}

        {students.length === 0 && !showForm ? (
          <div style={styles.panel}>
            <div style={styles.empty}>No chess students registered yet.</div>
          </div>
        ) : (
          students
            .sort((a, b) => (b.rating || 0) - (a.rating || 0))
            .map((s, idx) => {
              const tier = getTier(s.rating || DEFAULT_RATING);
              const levelStyle = getLevelBadge(s.level);
              const isExpanded = expandedId === s.id;
              return (
                <div key={s.id} style={styles.studentCard}>
                  <div style={styles.studentHeader}>
                    <div style={styles.rankBadge}>#{idx + 1}</div>
                    <div style={styles.studentIcon}></div>
                    <div style={styles.studentInfo}>
                      <div style={styles.studentName}>{s.name}</div>
                      <div style={styles.studentMeta}>
                        {s.school} · 🎂 {s.age || '—'} · {s.studentClass || '—'}
                      </div>
                    </div>
                    <span style={{ ...styles.levelBadge, background: levelStyle.bg, color: levelStyle.color }}>
                      {s.level}
                    </span>
                    <div style={styles.ratingBox}>
                      <div style={{ ...styles.ratingValue, color: tier.color }}>{s.rating || DEFAULT_RATING}</div>
                      <div style={styles.ratingTier}>{tier.swahili}</div>
                    </div>
                    <button style={styles.btnEdit} onClick={() => handleEdit(s)} title="Edit"></button>
                    <button style={styles.btnDelete} onClick={() => handleDelete(s.id)} title="Delete">Futa</button>
                  </div>

                  <div style={styles.statsRow}>
                    <div style={styles.statPill}>{s.wins || 0} Wins</div>
                    <div style={styles.statPill}>💔 {s.losses || 0} Losses</div>
                    <div style={styles.statPill}>{s.draws || 0} Draws</div>
                    <div style={styles.statPill}>{s.gamesPlayed || 0} Games</div>
                    <button style={styles.recordsToggle} onClick={() => toggleRecords(s.id)}>
                      {isExpanded ? 'Hide game records ▲' : 'View game records ▼'}
                    </button>
                  </div>

                  {isExpanded && (
                    <div style={styles.recordsBox}>
                      {recordsLoading && !records[s.id] ? (
                        <div style={styles.empty}>Loading...</div>
                      ) : (records[s.id] || []).length === 0 ? (
                        <div style={styles.empty}>No finished games yet.</div>
                      ) : (
                        records[s.id].map(g => {
                          const won = g.winner === g.color;
                          const drew = g.winner === 'draw';
                          const opponent = g.color === 'white' ? g.black?.name : g.white?.name;
                          return (
                            <div key={g.id} style={styles.recordRow}>
                              <span style={{
                                ...styles.recordResult,
                                color: drew ? '#6B7280' : won ? '#14452F' : '#C62828',
                              }}>
                                {drew ? 'Draw' : won ? 'Won' : '💔 Lost'}
                              </span>
                              <span style={styles.recordOpponent}>vs {opponent || 'Opponent'}</span>
                              <span style={styles.recordReason}>{g.resultReason}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })
        )}
      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '14px' },
  pageTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  pageSub: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px' },
  btnPrimary: { background: 'linear-gradient(135deg, #F57C00, #FFA726)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 14px rgba(245,124,0,0.25)' },
  successMsg: { background: '#EAF3EC', border: '1px solid #BBD8C4', color: '#14452F', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '20px' },
  errorMsg: { background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '20px' },
  panel: { background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)', marginBottom: '20px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#6B7280', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #F3F4F6', fontWeight: '600' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  formLabel: { fontSize: '12px', color: '#6B7280', fontWeight: '500' },
  formInput: { padding: '11px 14px', borderRadius: '9px', border: '1.5px solid #E5E7EB', background: '#FAF9F5', fontFamily: 'inherit', fontSize: '13.5px', color: '#1C1C1E', outline: 'none' },
  submitBtn: { width: '100%', padding: '13px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', color: '#fff', border: 'none', borderRadius: '10px', fontFamily: 'inherit', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 14px rgba(20,69,47,0.25)' },
  empty: { textAlign: 'center', color: '#6B7280', fontSize: '13px', padding: '20px 0' },
  studentCard: { background: '#fff', borderRadius: '16px', padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)', marginBottom: '14px' },
  studentHeader: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  rankBadge: { fontSize: '12px', fontWeight: '700', color: '#9CA3AF', width: '26px' },
  studentIcon: { width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #EAF3EC, #CFE3D4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 },
  studentInfo: { flex: 1, minWidth: '160px' },
  studentName: { fontSize: '15px', fontWeight: '700', color: '#1C1C1E' },
  studentMeta: { fontSize: '11.5px', color: '#6B7280', marginTop: '2px' },
  levelBadge: { fontSize: '11px', padding: '4px 10px', borderRadius: '20px', fontWeight: '600' },
  ratingBox: { textAlign: 'right', minWidth: '80px' },
  ratingValue: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '18px', fontWeight: '700' },
  ratingTier: { fontSize: '10px', color: '#6B7280' },
  btnEdit: { background: '#E3F2FD', color: '#1565C0', border: 'none', padding: '7px 10px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },
  btnDelete: { background: '#FFEBEE', color: '#C62828', border: 'none', padding: '7px 10px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },
  statsRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #F3F4F6' },
  statPill: { fontSize: '11.5px', color: '#374151', background: '#F9FAFB', padding: '5px 10px', borderRadius: '20px', border: '1px solid #F3F4F6' },
  recordsToggle: { marginLeft: 'auto', background: 'transparent', border: 'none', color: '#14452F', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  recordsBox: { marginTop: '12px', background: '#F9FAFB', borderRadius: '10px', padding: '10px 14px', border: '1px solid #F3F4F6' },
  recordRow: { display: 'flex', gap: '10px', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #EEF0EF', fontSize: '12.5px', flexWrap: 'wrap' },
  recordResult: { fontWeight: '700', minWidth: '70px' },
  recordOpponent: { color: '#374151', flex: 1 },
  recordReason: { color: '#9CA3AF', fontSize: '11px', textTransform: 'capitalize' },
};
